import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Award, MapPin, CreditCard } from 'lucide-react';
const features = [{
  icon: Sparkles,
  title: 'Latest Technology',
  description: 'State-of-the-art equipment for optimal results'
}, {
  icon: Award,
  title: 'Board-Certified Experts',
  description: 'Experienced surgeons with proven track records'
}, {
  icon: MapPin,
  title: '5 Locations Nationwide',
  description: 'Conveniently located across major U.S. cities'
}, {
  icon: CreditCard,
  title: 'Flexible Financing',
  description: 'Affordable payment plans available'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function WhyDermaclinic() {
  return <section className="w-full py-20 lg:py-24 bg-gradient-to-br from-white via-brand-aqua/20 to-white relative overflow-hidden" data-id="element-356">
      {/* Premium decorative elements with gold */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-357">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-gold/10 rounded-full blur-3xl" data-id="element-358" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-brand-primary/10 rounded-full blur-3xl" data-id="element-359" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-360">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-361">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-gold/10 rounded-full mb-4" data-id="element-362">
            <span className="w-2 h-2 bg-brand-gold rounded-full" data-id="element-363" />
            <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider" data-id="element-364">
              Why Choose Us
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-navy mb-6" data-id="element-365">
            The{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-gold" data-id="element-366">
              Dermaclinic
            </span>{' '}
            Difference
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-367">
            Premium aesthetic care with proven results and unmatched expertise
          </p>
        </motion.div>

        {/* Features grid - Premium cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" data-id="element-368">
          {features.map((feature, index) => <motion.div key={feature.title} variants={itemVariants} className="group relative bg-white rounded-3xl p-8 shadow-xl border-2 border-transparent transition-all duration-300 hover:border-brand-gold/30 hover:shadow-2xl hover:-translate-y-2" data-id="element-369">
              {/* Gold accent line */}
              <div className="absolute top-0 left-8 right-8 h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-370" />

              {/* Icon with premium gradient */}
              <div className="relative w-16 h-16 mb-6" data-id="element-371">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-primary to-brand-gold rounded-2xl opacity-10 group-hover:opacity-20 transition-opacity duration-300" data-id="element-372" />
                <div className="relative w-full h-full bg-gradient-to-br from-brand-primary to-brand-gold rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300" data-id="element-373">
                  <feature.icon className="w-8 h-8 text-white" data-id="element-374" />
                </div>
              </div>

              <h3 className="text-xl font-bold text-brand-navy mb-3 group-hover:text-brand-primary transition-colors duration-300" data-id="element-375">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-376">
                {feature.description}
              </p>

              {/* Premium shine effect */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" data-id="element-377" />
            </motion.div>)}
        </motion.div>

        {/* Premium stats bar */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mt-16 bg-gradient-to-r from-brand-navy via-brand-deep to-brand-navy rounded-3xl p-8 shadow-2xl" data-id="element-378">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-379">
            <div className="text-center" data-id="element-380">
              <p className="text-4xl font-bold text-brand-gold mb-2" data-id="element-381">10,000+</p>
              <p className="text-white/80 text-sm" data-id="element-382">Happy Patients</p>
            </div>
            <div className="text-center" data-id="element-383">
              <p className="text-4xl font-bold text-brand-gold mb-2" data-id="element-384">15+</p>
              <p className="text-white/80 text-sm" data-id="element-385">Years Experience</p>
            </div>
            <div className="text-center" data-id="element-386">
              <p className="text-4xl font-bold text-brand-gold mb-2" data-id="element-387">98%</p>
              <p className="text-white/80 text-sm" data-id="element-388">Satisfaction Rate</p>
            </div>
            <div className="text-center" data-id="element-389">
              <p className="text-4xl font-bold text-brand-gold mb-2" data-id="element-390">5</p>
              <p className="text-white/80 text-sm" data-id="element-391">Premium Locations</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}